import { useState, useEffect } from "react";
import { type Lead, type LeadCategory, type LeadStatus } from "../../lib/lead-types.ts";
import { saveLead } from "../../lib/lead-storage.ts";
import { toast } from "../Toast.tsx";

interface Props {
  isOpen: boolean;
  lead: Lead | null;
  onClose: () => void;
  onSaved: (lead: Lead) => void;
}

const CATEGORY_OPTIONS: { value: LeadCategory; label: string }[] = [
  { value: "expense_management", label: "Expense Management & Cards" },
  { value: "saas_pricing", label: "SaaS Localized Pricing & PPP" },
  { value: "invoicing_payroll", label: "Invoicing & Cross-Border Payroll" },
  { value: "ecommerce", label: "E-Commerce & Storefronts" },
  { value: "fintech", label: "Fintech & Banking" },
  { value: "other", label: "Other / General" },
];

const STATUS_OPTIONS: { value: LeadStatus; label: string }[] = [
  { value: "new", label: "New (Not Contacted)" },
  { value: "contacted", label: "Contacted (Outreach Sent)" },
  { value: "follow_up_needed", label: "Follow-up Needed" },
  { value: "replied", label: "Replied / Engaged" },
  { value: "converted", label: "Converted (Active App User)" },
  { value: "not_interested", label: "Not Interested / Archive" },
];

export function LeadEditorModal({ isOpen, lead, onClose, onSaved }: Props) {
  const [company, setCompany] = useState("");
  const [website, setWebsite] = useState("");
  const [email, setEmail] = useState("");
  const [contactName, setContactName] = useState("");
  const [category, setCategory] = useState<LeadCategory>("expense_management");
  const [status, setStatus] = useState<LeadStatus>("new");
  const [useCase, setUseCase] = useState("");
  const [painPoint, setPainPoint] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (lead) {
      setCompany(lead.company);
      setWebsite(lead.website || "");
      setEmail(lead.email);
      setContactName(lead.contactName || "");
      setCategory(lead.category);
      setStatus(lead.status);
      setUseCase(lead.useCase || "");
      setPainPoint(lead.painPoint || "");
      setNotes(lead.notes || "");
    } else {
      setCompany("");
      setWebsite("");
      setEmail("");
      setContactName("");
      setCategory("expense_management");
      setStatus("new");
      setUseCase("");
      setPainPoint("");
      setNotes("");
    }
  }, [lead, isOpen]);

  if (!isOpen) return null;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!company.trim()) {
      toast("Company name is required.");
      return;
    }
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      toast("Please provide a valid contact email address.");
      return;
    }

    let normalizedWebsite = website.trim();
    if (normalizedWebsite && !/^https?:\/\//i.test(normalizedWebsite)) {
      normalizedWebsite = `https://${normalizedWebsite}`;
    }

    try {
      const saved = saveLead({
        ...(lead ? { id: lead.id } : {}),
        company: company.trim(),
        website: normalizedWebsite,
        email: email.trim(),
        contactName: contactName.trim() || undefined,
        category,
        status,
        followUpCount: lead?.followUpCount ?? 0,
        useCase: useCase.trim() || undefined,
        painPoint: painPoint.trim() || undefined,
        notes: notes.trim() || undefined,
        lastContactedAt: lead?.lastContactedAt ?? null,
      });

      toast(lead ? "Lead updated successfully." : "New lead added to directory.");
      onSaved(saved);
      onClose();
    } catch (err) {
      toast(err instanceof Error ? err.message : "Failed to save lead.");
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-box modal-lg"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="modal-head">
          <div>
            <h3>{lead ? "Edit Prospective Lead" : "Add New Prospective Lead"}</h3>
            <p className="muted text-xs">
              Maintain lead details for manual 1-by-1 outreach and conversion tracking.
            </p>
          </div>
          <button type="button" className="btn btn-ghost btn-icon" onClick={onClose}>
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body space-y-3">
            <div className="grid-2col">
              <div className="form-group">
                <label className="form-label required">Company / App Name</label>
                <input
                  type="text"
                  className="input w-full"
                  placeholder="e.g. SmartSpend Expense"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Website / App Link</label>
                <input
                  type="text"
                  className="input w-full"
                  placeholder="e.g. smartspend.app or https://..."
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                />
              </div>
            </div>

            <div className="grid-2col">
              <div className="form-group">
                <label className="form-label required">Contact Email</label>
                <input
                  type="email"
                  className="input w-full"
                  placeholder="e.g. alex@smartspend.app"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Contact Person Name</label>
                <input
                  type="text"
                  className="input w-full"
                  placeholder="e.g. Alex (Founder / CTO)"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                />
              </div>
            </div>

            <div className="grid-2col">
              <div className="form-group">
                <label className="form-label">Industry / Category</label>
                <select
                  className="select w-full"
                  value={category}
                  onChange={(e) => setCategory(e.target.value as LeadCategory)}
                >
                  {CATEGORY_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Pipeline Status</label>
                <select
                  className="select w-full"
                  value={status}
                  onChange={(e) => setStatus(e.target.value as LeadStatus)}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Use Case / Feature Focus</label>
              <input
                type="text"
                className="input w-full"
                placeholder="e.g. Multi-currency receipt reconciliation and corporate card FX rates"
                value={useCase}
                onChange={(e) => setUseCase(e.target.value)}
              />
              <span className="form-hint">
                Populates the <code>{"{{use_case}}"}</code> variable in outreach templates.
              </span>
            </div>

            <div className="form-group">
              <label className="form-label">Specific Pain Point</label>
              <input
                type="text"
                className="input w-full"
                placeholder="e.g. High FX spreads or delayed daily exchange rate feeds"
                value={painPoint}
                onChange={(e) => setPainPoint(e.target.value)}
              />
              <span className="form-hint">
                Populates the <code>{"{{pain_point}}"}</code> variable in outreach templates.
              </span>
            </div>

            <div className="form-group">
              <label className="form-label">Internal Discovery Notes</label>
              <textarea
                className="textarea w-full"
                rows={3}
                placeholder="e.g. Discovered on Product Hunt. Mentioned in changelog that they are adding European expansion."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>

          <div className="modal-foot">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              {lead ? "Update Lead" : "Save Lead"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
